// External Libraries
import { useImmerReducer } from "use-immer";
import { v4 } from "uuid";

// Internal Utilities
import { deepCopy } from "../../../../../helper";

// Constants for actions
export const UPDATE_TITLE = "UPDATE_TITLE";
export const UPDATE_DUE_DATE = "UPDATE_DUE_DATE";
export const UPDATE_REMINDERS = "UPDATE_REMINDERS";
export const UPDATE_RECURRING_REQUEST = "UPDATE_RECURRING_REQUEST";
export const UPDATE_EMAIL_DESCRIPTION = "UPDATE_EMAIL_DESCRIPTION";
export const UPDATE_CURRENT_SELECT_TEMPLATE = "UPDATE_CURRENT_SELECT_TEMPLATE";
export const ADD_REQUIRED_DOCUMENT = "ADD_REQUIRED_DOCUMENT";
export const UPDATE_REQUIRED_DOCUMENT = "UPDATE_REQUIRED_DOCUMENT";
export const REMOVE_REQUIRED_DOCUMENT = "REMOVE_REQUIRED_DOCUMENT";
export const UPDATE_CURRENT_SELECT_MAILING_LIST =
  "UPDATE_CURRENT_SELECT_MAILING_LIST";
export const ADD_USER_TO_MAILING_LIST = "ADD_USER_TO_MAILING_LIST";
export const REMOVE_USER_FROM_MAILING_LIST = "REMOVE_USER_FROM_MAILING_LIST";
export const RESET_TO_INITIAL_STATE = "RESET_TO_INITIAL_STATE";
export const SET_ENTIRE_STATE = "SET_ENTIRE_STATE";
export const ADD_ENTIRE_REQUIRED_DOCS = "ADD_ENTIRE_REQUIRED_DOCS";
export const ADD_ENTIRE_USERS = "ADD_ENTIRE_USERS";
export const ADD_ENTIRE_USERS_FROM_EXCEL = "ADD_ENTIRE_USERS_FROM_EXCEL";
export const UPDATE_REQUIRED_DOCS_FROM_TEMPLATE =
  "UPDATE_REQUIRED_DOCS_FROM_TEMPLATE";
export const UPDATE_USERS_FROM_MAILING_LIST = "UPDATE_USERS_FROM_MAILING_LIST";
export const TOGGLE_TEMPLATE_MODIFIED = "TOGGLE_TEMPLATE_MODIFIED";
export const TOGGLE_MAILING_LIST_MODIFIED = "TOGGLE_MAILING_LIST_MODIFIED";
// Initial state
const initialState = {
  // Id will be used for editing the request
  id: v4(),
  title: "",
  dueDate: "",
  reminders: "Weekly",
  recurringRequest: "",
  emailDescription: "",
  currentSelectTemplate: {
    id: "",
    title: "",
  },
  requiredDocs: [getEmptyRequiredDocuments()],
  currentSelectMailingList: {
    id: "",
    title: "",
  },
  requiredDocsFromTemplate: [],
  usersFromMailingList: [],
  users: [],
  initialStateForEdit: {
    currentSelectTemplate: {},
    requiredDocs: [],
    requiredDocsFromTemplate: [],
    usersFromMailingList: [],
    users: [],
  },
  isTemplateModified: false,
  isMailingListModified: false,
};

// Reducer function using useImmerReducer
const createRequestFormReducer = (draft, action) => {
  switch (action.type) {
    case UPDATE_TITLE:
      draft.title = action.payload;
      break;
    case UPDATE_DUE_DATE:
      draft.dueDate = action.payload;
      break;
    case UPDATE_REMINDERS:
      draft.reminders = action.payload;
      break;
    case UPDATE_RECURRING_REQUEST:
      draft.recurringRequest = action.payload;
      break;
    case UPDATE_EMAIL_DESCRIPTION:
      draft.emailDescription = action.payload;
      break;
    case UPDATE_CURRENT_SELECT_TEMPLATE:
      draft.currentSelectTemplate = action.payload;
      draft.isTemplateModified = false;
      break;
    case UPDATE_CURRENT_SELECT_MAILING_LIST:
      draft.currentSelectMailingList = action.payload;
      draft.isMailingListModified = false;
      break;
    case ADD_ENTIRE_USERS:
      draft.users = action.payload;
      break;
    case UPDATE_REQUIRED_DOCS_FROM_TEMPLATE:
      draft.requiredDocsFromTemplate = action.payload;
      break;
    case UPDATE_USERS_FROM_MAILING_LIST:
      draft.usersFromMailingList = action.payload;
      break;
    case ADD_ENTIRE_USERS_FROM_EXCEL:
      draft.users = action.payload;
      break;
    case ADD_ENTIRE_REQUIRED_DOCS:
      draft.requiredDocs = action.payload;
      break;

    case ADD_REQUIRED_DOCUMENT:
      draft.requiredDocs.push(action.payload);
      break;

    case UPDATE_REQUIRED_DOCUMENT:
      const indexToUpdate = draft.requiredDocs.findIndex(
        (doc) => doc.documentId === action.payload.documentId
      );
      if (indexToUpdate !== -1) {
        draft.requiredDocs[indexToUpdate] = {
          ...draft.requiredDocs[indexToUpdate],
          ...action.payload,
        };
      }
      break;

    case REMOVE_REQUIRED_DOCUMENT:
      draft.requiredDocs = draft.requiredDocs.filter(
        (doc) => doc.documentId !== action.payload
      );
      break;

    case ADD_USER_TO_MAILING_LIST:
      draft.users.push(action.payload);
      break;

    case REMOVE_USER_FROM_MAILING_LIST:
      draft.users = draft.users.filter((user) => user.email !== action.payload);
      break;
    case RESET_TO_INITIAL_STATE:
      return deepCopy(initialState);
    case SET_ENTIRE_STATE:
      return deepCopy(action.payload);
    case TOGGLE_TEMPLATE_MODIFIED:
      draft.isTemplateModified = action.payload;
      break;
    case TOGGLE_MAILING_LIST_MODIFIED:
      draft.isMailingListModified = action.payload;
      break;
    default:
      return draft;
  }
};

// Custom hook using useImmerReducer
const useRequestMutationForm = () => {
  const [state, dispatch] = useImmerReducer(
    createRequestFormReducer,
    deepCopy(initialState)
  );

  const onChange = (type, payload) => {
    dispatch({ type, payload });
  };

  return { state, onChange };
};

export default useRequestMutationForm;

/**
 * Generates an empty required documents template with a unique document ID.
 *
 * @returns {object} An object representing an empty required document template.
 *                   It includes properties such as 'documentId', 'documentName', 'documentFile', and 'documentFileName'.
 */
export function getEmptyRequiredDocuments() {
  return {
    documentId: v4() + "&&&&SUFFIX_FOR_NEW_DOCUMENT",
    documentName: "",
    documentFile: null,
    documentFileName: "",
  };
}
